import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Source, Layer, NavigationControl, Marker } from 'react-map-gl/maplibre';
import { MapPin } from 'lucide-react';
import { getViewModeStyle } from '../lib/map-styles';
import {
  buildGeoRef, previewGeoJSON, localFootprint, searchCrs, crsLabel, crsInfo, lineSpacing, lineEnds,
  normalizeCrs, crsUnits, registerServerCrs, LOCAL_CRS, fromWgs84, toWgs84,
} from '../lib/georef';
import { useTheme } from '../contexts/ThemeContext';
import { useOrg } from '../contexts/OrgContext';
import { crsApi, projectsApi, datasetsApi } from '../lib/api';
import { flyToResult, placeExtent, labelHead } from '../lib/geocode';
import { useOptionalToast } from './Toast';
import AddressSearch from './AddressSearch';

const MODES = [
  { id: 'file', label: 'Coordinates in file' },
  { id: 'tie_points', label: 'Start and end points' },
  { id: 'origin_azimuth', label: 'Anchor + azimuth' },
  { id: 'gcps', label: 'Tie points table' },
];

const PICK_HINTS = {
  start: 'Click the map to set the first electrode',
  end: 'Click the map to set the last electrode',
  origin: 'Click the map to set the anchor',
  azimuth: 'Click the map where the line should point to',
};

const num = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));
const finite = (v) => v !== undefined && Number.isFinite(v);
const fmt = (v, d = 2) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(d) : '—');

const NO_ELECTRODES = []; // stable defaults: a fresh [] per render would recompute `preview` every render
const NO_CELLS = [];

const crsLookupCache = {}; // normalised code -> server answer (`Map` is shadowed by react-map-gl here)

/**
 * Server-side CRS facts (name, units, proj string) for the selected CRS; null until known
 * or when the lookup fails. The proj string is registered with proj4 before the state
 * updates, so a CRS outside the bundled list gets the same live preview as a bundled one.
 */
function useServerCrs(crs) {
  const code = normalizeCrs(crs);
  const [info, setInfo] = useState(() => crsLookupCache[code] || null);
  useEffect(() => {
    if (code === LOCAL_CRS) { setInfo(null); return undefined; }
    if (crsLookupCache[code]) { registerServerCrs(crsLookupCache[code]); setInfo(crsLookupCache[code]); return undefined; }
    let active = true;
    const t = setTimeout(() => {
      crsApi.lookup(code)
        .then(res => { registerServerCrs(res.data); crsLookupCache[code] = res.data; if (active) setInfo(res.data); })
        .catch(() => { if (active) setInfo(null); }); // fall back to the bundled list
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [code]);
  return info;
}

/**
 * Start/end tie points -> the Anchor + azimuth form: anchor = the first electrode at the start
 * point, azimuth = the line's bearing (clockwise from north), spacing = the effective one. Lets
 * "Rotate" start from the current placement. null when the stored georef has no usable tie points.
 */
export function anchorFromTiePoints(inp, sorted) {
  const a = inp?.start?.world;
  const b = inp?.end?.world;
  if (!a || !b || ![...a, ...b].every(Number.isFinite) || !sorted.length) return null;
  const dE = b[0] - a[0];
  const dN = b[1] - a[1];
  const len = Math.hypot(dE, dN);
  if (!(len > 0)) return null;
  const azimuth = Math.round((((Math.atan2(dE, dN) * 180) / Math.PI) + 360) % 360 * 100) / 100;
  const fs = lineSpacing(sorted.map(e => e.x));
  const span = sorted[sorted.length - 1].x - sorted[0].x;
  const anchorSpacing = fs && span > 0 ? Math.round((len / span) * fs * 1e6) / 1e6 : '';
  return { origin: { E: a[0], N: a[1] }, originElectrode: sorted[0].id, azimuth, anchorSpacing: anchorSpacing && Math.abs(anchorSpacing - fs) > 1e-6 ? anchorSpacing : '' };
}

/** Initial dialog state from a stored georef (re-editing) or sensible defaults. `initialMode` opens a given tab. */
function initialState(georef, crsGuess, electrodes, initialMode = null) {
  const inp = georef?.inputs || {};
  const sorted = [...electrodes].sort((a, b) => a.x - b.x);
  const fileLooksWorld = crsGuess && crsGuess.kind !== 'local';
  const fromTie = initialMode === 'origin_azimuth' && georef?.mode === 'tie_points' ? anchorFromTiePoints(inp, sorted) : null;
  const state = {
    mode: georef?.mode && georef.mode !== 'local' ? georef.mode : (fileLooksWorld ? 'file' : 'tie_points'),
    crs: georef?.crs && georef.crs !== LOCAL_CRS ? georef.crs : (crsGuess?.crs && crsGuess.crs !== LOCAL_CRS ? crsGuess.crs : 'EPSG:32614'),
    start: { E: inp.start?.world?.[0] ?? '', N: inp.start?.world?.[1] ?? '' },
    end: { E: inp.end?.world?.[0] ?? '', N: inp.end?.world?.[1] ?? '' },
    fitScale: inp.fit_scale ?? true,
    spacing: inp.spacing != null ? Math.round(Number(inp.spacing) * 1e6) / 1e6 : '',
    origin: { E: inp.origin?.world?.[0] ?? '', N: inp.origin?.world?.[1] ?? '' },
    originElectrode: sorted[0]?.id ?? '',
    azimuth: inp.azimuth_deg ?? 90,
    anchorSpacing: (() => {
      const fs = lineSpacing(electrodes.map(e => e.x));
      return inp.scale && inp.scale !== 1 && fs ? Math.round(Number(inp.scale) * fs * 1e6) / 1e6 : '';
    })(),
    anchorFitSpacing: false,
    gcps: inp.gcps?.length ? inp.gcps.map(g => ({ electrode: g.id ?? '', E: g.E, N: g.N }))
      : [0, 1, 2].map(i => ({ electrode: sorted[Math.round((i * (sorted.length - 1)) / 2)]?.id ?? '', E: '', N: '' })),
    elevationOffset: georef?.elevation_offset ?? 0,
  };
  if (initialMode) state.mode = initialMode;
  return fromTie ? { ...state, ...fromTie } : state;
}

/** Dialog state -> build spec (the same JSON the backend PUT .../georef accepts). */
export function specFromState(s, electrodes, xRange = null) {
  const fileSp = lineSpacing(electrodes.map(q => q.x));
  const base = { mode: s.mode, crs: normalizeCrs(s.crs), elevation_offset: num(s.elevationOffset) || 0 };
  if (s.mode === 'tie_points') {
    // Models without electrodes: anchor the two points to the model x extent.
    const ends = lineEnds(electrodes, xRange);
    const local = ends.fromModelExtent ? { start_local: [ends.startX, 0], end_local: [ends.endX, 0] } : {};
    return { ...base, ...local, start: { E: num(s.start.E), N: num(s.start.N) }, end: { E: num(s.end.E), N: num(s.end.N) },
      fit_scale: !!s.fitScale, ...(num(s.spacing) ? { spacing: num(s.spacing) } : {}) };
  }
  if (s.mode === 'origin_azimuth') {
    const e = electrodes.find(q => String(q.id) === String(s.originElectrode));
    const asp = num(s.anchorSpacing);
    const scale = asp && fileSp ? asp / fileSp : 1;
    return { ...base, origin: { E: num(s.origin.E), N: num(s.origin.N) }, azimuth_deg: num(s.azimuth),
      ...(e ? { origin_local: [e.x, e.y || 0] } : {}), ...(Math.abs(scale - 1) > 1e-12 ? { scale } : {}) };
  }
  if (s.mode === 'gcps') {
    return { ...base, gcps: s.gcps.filter(g => g.E !== '' && g.N !== '' && g.electrode !== '')
      .map(g => ({ electrode: g.electrode, E: num(g.E), N: num(g.N) })) };
  }
  return base;
}

/**
 * GeorefDialog — "define coordinates" for any imported product (raw survey or
 * 1D/2D/3D model, any instrument or software). Builds one GeoRef with the same
 * maths as the backend (lib/georef.js) and previews electrodes + footprint live.
 * docs/GEOREFERENCING_AND_MODEL_IMPORT.md, section 9.
 *
 * Props:
 *   title, initialMode ('tie_points' | 'origin_azimuth' | ... : the tab to open on; stored tie points prefill the anchor), electrodes [{id,x,y,z}] (local), geometryKind, cells? (for the grid footprint),
 *   initialGeoref (stored dict | null), crsGuess ({kind, crs}),
 *   onSave(georef, spec) -> Promise|void, onClose()
 *   datasetRef ({ id, projectId } | null), onNavigate(path): after "New project here" and a saved
 *   georeference the dialog sends the user to the map of the new project (see handleSave).
 */
export default function GeorefDialog({ title = 'Define coordinates', electrodes = NO_ELECTRODES, geometryKind = 'line', cells = NO_CELLS,
  initialGeoref = null, initialMode = null, crsGuess = null, xRange = null, datasetRef = null, onNavigate, onSave, onClose }) {
  const { C: themeC, theme } = useTheme();
  const C = themeC;
  const [s, setS] = useState(() => initialState(initialGeoref, crsGuess, electrodes, initialMode));
  const [crsQuery, setCrsQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const mapRef = useRef(null);
  const { showToast } = useOptionalToast();
  const { selectedOrgId } = useOrg() || {};
  const [picking, setPicking] = useState(null); // 'start' | 'end' | 'origin' | 'azimuth' while the next map click is wanted
  const [pickError, setPickError] = useState(null);
  const [basemap, setBasemap] = useState(() => {
    try { const v = localStorage.getItem('georef.basemap'); return ['map', 'satellite', 'topo'].includes(v) ? v : 'map'; } catch { return 'map'; }
  });
  const chooseBasemap = (b) => { setBasemap(b); try { localStorage.setItem('georef.basemap', b); } catch { /* storage unavailable */ } };
  const [pin, setPin] = useState(null); // the address search result shown on the preview map
  const [place, setPlace] = useState(null); // { label, lon, lat, bbox } a new project could be created at
  const [np, setNp] = useState(null); // new-project form { name, description, busy, error } | null
  const [createdProject, setCreatedProject] = useState(null);
  const [moved, setMoved] = useState(false);
  const [moveError, setMoveError] = useState(null);

  const set = (patch) => setS(prev => ({ ...prev, ...patch }));
  const fileSpacing = useMemo(() => lineSpacing(electrodes.map(e => e.x)), [electrodes]);
  const ends = useMemo(() => lineEnds(electrodes, xRange), [electrodes, xRange]);
  const crsOptions = useMemo(() => searchCrs(crsQuery).slice(0, 12), [crsQuery]);
  const serverCrs = useServerCrs(s.crs);
  const browserCrs = crsInfo(s.crs).known; // true once the server's proj string is registered
  const units = crsUnits(s.crs, serverCrs);
  const localCrs = normalizeCrs(s.crs) === LOCAL_CRS;
  const pickOff = localCrs ? 'A local grid has no map position, so there is nothing to click. Pick a real coordinate system first.'
    : !browserCrs ? `No live map for ${s.crs} yet, so a click cannot be turned into E/N.` : null;
  const pickDecimals = units.label === 'deg' ? 7 : 2;

  const spec = useMemo(() => specFromState(s, electrodes, xRange), [s, electrodes, xRange]);
  const { georef, preview, error } = useMemo(() => {
    try {
      const g = buildGeoRef(spec, electrodes);
      if (!browserCrs) return { georef: g, preview: null, error: null };
      const fp = localFootprint({ geometryKind, electrodes, cells });
      return { georef: g, preview: previewGeoJSON(electrodes, g, fp), error: null };
    } catch (e) {
      return { georef: null, preview: null, error: e.message };
    }
  }, [spec, electrodes, geometryKind, cells, browserCrs]);

  // Fit the preview map to the electrodes when they move (not on unrelated re-renders, which would
  // cancel an address fly-to or a user pan): refit only when the rounded coordinates change.
  const lastFitSig = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current?.getMap?.();
    if (!map || !preview?.features?.length) return;
    const sig = JSON.stringify(preview.features.map(f => f.geometry.coordinates), (k, v) => (typeof v === 'number' ? Math.round(v * 1e6) / 1e6 : v));
    if (sig === lastFitSig.current) return;
    let w = Infinity, so = Infinity, e = -Infinity, n = -Infinity;
    preview.features.forEach(f => {
      const cs = f.geometry.type === 'Point' ? [f.geometry.coordinates]
        : f.geometry.type === 'Polygon' ? f.geometry.coordinates[0] : f.geometry.coordinates;
      cs.forEach(([lon, lat]) => { w = Math.min(w, lon); e = Math.max(e, lon); so = Math.min(so, lat); n = Math.max(n, lat); });
    });
    if (Number.isFinite(w)) {
      try { map.fitBounds([[w, so], [e, n]], { padding: 40, maxZoom: 19, duration: 0 }); lastFitSig.current = sig; } catch { /* map not ready: retry on the next change */ }
    }
  }, [preview, mapReady]);

  // Midpoint of the line in E/N when both ends are set (handle for dragging the whole line).
  const midMarker = useMemo(() => {
    if (s.mode !== 'tie_points' || !browserCrs || localCrs) return null;
    const Es = num(s.start.E), Ns = num(s.start.N), Ee = num(s.end.E), Ne = num(s.end.N);
    if (![Es, Ns, Ee, Ne].every(finite)) return null;
    const Em = (Es + Ee) / 2, Nm = (Ns + Ne) / 2;
    try { const [lon, lat] = toWgs84(Em, Nm, s.crs); return Number.isFinite(lon) && Number.isFinite(lat) ? { lon, lat, E: Em, N: Nm } : null; } catch { return null; }
  }, [s, browserCrs, localCrs]);

  // Translate the whole line by (dE, dN), keeping length and azimuth.
  const shiftLine = (dE, dN) => {
    const r = (v) => Number(v.toFixed(pickDecimals));
    set({ start: { E: r(num(s.start.E) + dE), N: r(num(s.start.N) + dN) }, end: { E: r(num(s.end.E) + dE), N: r(num(s.end.N) + dN) } });
    setPickError(null);
  };
  const dragMid = (lngLat) => {
    if (!midMarker) return;
    try {
      const [Em, Nm] = fromWgs84(lngLat.lng, lngLat.lat, s.crs);
      if (Number.isFinite(Em) && Number.isFinite(Nm)) shiftLine(Em - midMarker.E, Nm - midMarker.N);
    } catch (e) { setPickError(e.message); }
  };
  const moveLineHere = () => {
    if (!place || pickOff) return;
    try {
      const [Ep, Np] = fromWgs84(place.lon, place.lat, s.crs);
      const r = (v) => Number(v.toFixed(pickDecimals));
      if (s.mode === 'origin_azimuth') { set({ origin: { E: r(Ep), N: r(Np) } }); setPickError(null); return; }
      if (midMarker) { shiftLine(Ep - midMarker.E, Np - midMarker.N); return; }
      const { startX, endX } = lineEnds(electrodes, xRange);
      let L = endX - startX;
      if (!Number.isFinite(L) || L <= 0) L = 100;
      set({ start: { E: r(Ep - L / 2), N: r(Np) }, end: { E: r(Ep + L / 2), N: r(Np) } });
      setPickError(null);
    } catch (e) { setPickError(e.message); }
  };
  const canMoveLine = s.mode === 'tie_points' || s.mode === 'origin_azimuth';

  // Draggable markers: first/last electrode (start-end tab) or the origin.
  const markers = useMemo(() => {
    if (!browserCrs || localCrs) return [];
    const keys = s.mode === 'tie_points' ? ['start', 'end'] : s.mode === 'origin_azimuth' ? ['origin'] : [];
    return keys.flatMap((k) => {
      const E = num(s[k].E), N = num(s[k].N);
      if (!finite(E) || !finite(N)) return [];
      try { const [lon, lat] = toWgs84(E, N, s.crs); return Number.isFinite(lon) && Number.isFinite(lat) ? [{ key: k, lon, lat }] : []; } catch { return []; }
    });
  }, [s, browserCrs, localCrs]);

  const sortedXs = useMemo(() => [...electrodes].sort((a, b) => a.x - b.x), [electrodes]);
  const anchorElectrode = electrodes.find(q => String(q.id) === String(s.originElectrode));
  const effSpacing = num(s.anchorSpacing) || fileSpacing || 0;
  // Electrode farthest from the anchor along the line, as drawn in the preview (handle for rotating).
  const farMarker = useMemo(() => {
    if (s.mode !== 'origin_azimuth' || !preview || !anchorElectrode) return null;
    let far = null;
    electrodes.forEach(q => { if (!far || Math.abs(q.x - anchorElectrode.x) > Math.abs(far.x - anchorElectrode.x)) far = q; });
    if (!far || far.x === anchorElectrode.x) return null;
    const f = preview.features.find(q => q.properties?.kind === 'electrode' && String(q.properties.id) === String(far.id));
    return f ? { lon: f.geometry.coordinates[0], lat: f.geometry.coordinates[1], indexDistance: fileSpacing ? Math.abs(far.x - anchorElectrode.x) / fileSpacing : null } : null;
  }, [s.mode, preview, electrodes, anchorElectrode, fileSpacing]);

  // Bearing (degrees clockwise from north, 0-360) from the anchor E/N to a map point, in the CRS.
  const bearingTo = (lngLat) => {
    const E0 = num(s.origin.E), N0 = num(s.origin.N);
    if (!finite(E0) || !finite(N0)) throw new Error('Set the anchor position first');
    const [E, N] = fromWgs84(lngLat.lng, lngLat.lat, s.crs);
    if (!Number.isFinite(E) || !Number.isFinite(N)) throw new Error('That point is outside the coordinate system');
    const az = (Math.atan2(E - E0, N - N0) * 180) / Math.PI;
    const norm = Number((((az % 360) + 360) % 360).toFixed(2));
    return { az: norm === 360 ? 0 : norm, dist: Math.hypot(E - E0, N - N0) };
  };
  const dragAzimuth = (lngLat) => {
    try {
      const { az, dist } = bearingTo(lngLat);
      const patch = { azimuth: az };
      if (s.anchorFitSpacing && farMarker?.indexDistance && dist > 0) {
        const mpu = crsInfo(s.crs).metresPerUnit || 1;
        patch.anchorSpacing = Number(((dist * mpu) / farMarker.indexDistance).toFixed(pickDecimals));
      }
      set(patch);
      setPickError(null);
    } catch (e) { setPickError(e.message); }
  };

  const lngLatToField = (key, lngLat) => {
    try {
      const [x, y] = fromWgs84(lngLat.lng, lngLat.lat, s.crs);
      const r = (v) => Number(v.toFixed(pickDecimals));
      set({ [key]: { E: r(x), N: r(y) } });
      setPickError(null);
      setPlace(prev => ({ label: prev?.label || `Picked point (${lngLat.lat.toFixed(5)}, ${lngLat.lng.toFixed(5)})`, lon: lngLat.lng, lat: lngLat.lat, bbox: null }));
    } catch (e) {
      setPickError(e.message);
    }
  };
  const handleMapClick = (evt) => {
    if (!picking) return;
    const key = picking;
    setPicking(null);
    if (key === 'azimuth') {
      try { set({ azimuth: bearingTo(evt.lngLat).az }); setPickError(null); } catch (e) { setPickError(e.message); }
      return;
    }
    lngLatToField(key, evt.lngLat);
  };
  const handleSelectPlace = (r) => {
    setPin(r);
    setPlace({ label: r.label, lon: r.lon, lat: r.lat, bbox: r.bbox || null });
    setNp(null);
    flyToResult(mapRef, r);
  };

  const openNewProject = () => setNp({ name: labelHead(place.label), description: '', busy: false, error: null });
  const createProject = async () => {
    const name = np.name.trim();
    if (!name) { setNp({ ...np, error: 'Give the project a name' }); return; }
    setNp({ ...np, busy: true, error: null });
    try {
      const res = await projectsApi.create({ name, description: np.description.trim() || null, location: place.label, extent: placeExtent(place) }, selectedOrgId);
      const project = res.data;
      setCreatedProject(project);
      setNp(null);
      showToast(`Project "${project.name}" created`, 'success');
      // Link the dataset to the new project. The project stays created if this fails.
      if (datasetRef?.id) {
        try {
          await datasetsApi.update(datasetRef.id, { project_id: project.id });
          setMoved(true);
        } catch (e) {
          setMoveError(e?.response?.data?.detail || e.message || 'Could not move the dataset into the new project');
        }
      }
    } catch (e) {
      setNp(prev => ({ ...prev, busy: false, error: e?.response?.data?.detail || e.message || 'Could not create the project' }));
    }
  };

  const handleSave = async () => {
    if (!georef) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(georef, spec);
      if (createdProject && moved && datasetRef && onNavigate) {
        onNavigate(`/map?project=${createdProject.id}&dataset=${datasetRef.id}`);
      }
    } catch (e) {
      setSaveError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const d = georef?.derived || {};
  const sc = d.spacing_check;
  const inp = { width: '100%', fontSize: 12, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };
  const pair = (value, onChange, pickKey, labels = [`Easting (E) [${units.label}]`, `Northing (N) [${units.label}]`]) => (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        {['E', 'N'].map((k, i) => (
          <div key={k} style={{ flex: 1 }}>
            <label style={lbl}>{labels[i]}</label>
            <input type="number" value={value[k]} onChange={e => onChange({ ...value, [k]: e.target.value })} style={inp} />
          </div>
        ))}
      </div>
      <button type="button" disabled={!!pickOff} title={pickOff || 'Click the preview map to fill these two fields'}
        aria-pressed={picking === pickKey} onClick={() => { setPickError(null); setPicking(picking === pickKey ? null : pickKey); }}
        style={{ marginTop: 4, fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: pickOff ? 'not-allowed' : 'pointer', opacity: pickOff ? 0.5 : 1,
          background: picking === pickKey ? C.accentBg : C.bg4, color: picking === pickKey ? C.accent : C.text1,
          border: `1px solid ${picking === pickKey ? C.accentBorder : C.border}` }}>
        {picking === pickKey ? 'Click the map… (cancel)' : 'Pick on map'}
      </button>
    </div>
  );
  const anchorQuick = [['first', 'First', sortedXs[0]], ['middle', 'Middle', sortedXs[Math.floor((sortedXs.length - 1) / 2)]], ['last', 'Last', sortedXs[sortedXs.length - 1]]];
  const electrodeOptions = electrodes.map(e => <option key={e.id} value={e.id}>#{e.id} (x {fmt(e.x)})</option>);

  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(980px, 100%)', maxHeight: '92vh', overflow: 'auto', background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text0 }}>{title}</div>
            <div style={{ fontSize: 11, color: C.text2 }}>
              {electrodes.length} electrodes · {geometryKind}{fileSpacing ? ` · spacing in file ${fmt(fileSpacing, 3)} m` : ''}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: 16 }}>
          {/* Inputs */}
          <div style={{ flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            <div>
              <label style={lbl}>Coordinate system (CRS) — {units.name} [{units.label}]</label>
              <input placeholder="Search: UTM 14N, Texas, EPSG:32614…" value={crsQuery} onChange={e => setCrsQuery(e.target.value)} style={inp} />
              <select value={s.crs} onChange={e => set({ crs: e.target.value })} style={{ ...inp, marginTop: 4 }} size={Math.min(5, Math.max(2, crsOptions.length))}>
                {!crsOptions.some(o => o.code === s.crs) && <option value={s.crs}>{crsLabel(s.crs)} ({s.crs})</option>}
                {crsOptions.map(o => <option key={o.code} value={o.code}>{o.label} ({o.code})</option>)}
              </select>
              {!browserCrs && <div style={{ fontSize: 10, color: C.warnText, marginTop: 3 }}>No live preview for {s.crs}{serverCrs ? ` (${serverCrs.name}, ${serverCrs.units}; the server gave no usable projection string)` : ' yet (looking it up)'}; the server validates it on save.</div>}
            </div>

            <div>
              <label style={lbl}>Find the site</label>
              <AddressSearch onSelect={handleSelectPlace} />
            </div>

            <div role="tablist" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {MODES.map(m => (
                <button key={m.id} role="tab" aria-selected={s.mode === m.id} onClick={() => { setPicking(null); set({ mode: m.id }); }}
                  style={{ fontSize: 11, padding: '5px 9px', borderRadius: 6, cursor: 'pointer', border: `1px solid ${s.mode === m.id ? C.accentBorder : C.border}`, background: s.mode === m.id ? C.accentBg : C.bg4, color: s.mode === m.id ? C.accent : C.text1 }}>
                  {m.label}
                </button>
              ))}
            </div>

            {s.mode === 'file' && (
              <div style={{ fontSize: 11, color: C.text2, lineHeight: 1.6 }}>
                The electrode X/Y in the file are already eastings/northings (or lon/lat) in the CRS above. No transform is applied.
                {crsGuess?.kind === 'local' && <div style={{ color: C.warnText }}>These coordinates look local (small numbers). Use another tab unless you are sure.</div>}
              </div>
            )}

            {s.mode === 'tie_points' && (
              <>
                <div style={{ fontSize: 11, color: C.text2 }}>{ends.fromModelExtent ? 'Model start' : 'First electrode'} (x = {fmt(ends.startX)} m)</div>
                {pair(s.start, v => set({ start: v }), 'start')}
                <div style={{ fontSize: 11, color: C.text2 }}>{ends.fromModelExtent ? 'Model end' : 'Last electrode'} (x = {fmt(ends.endX)} m)</div>
                {pair(s.end, v => set({ end: v }), 'end')}
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Electrode spacing (m) — file: {fmt(fileSpacing, 3)}</label>
                    <input type="number" value={s.spacing} placeholder={fileSpacing ? String(Math.round(fileSpacing * 1e3) / 1e3) : ''} onChange={e => set({ spacing: e.target.value })} style={inp} />
                  </div>
                  <label style={{ flex: 1, fontSize: 11, color: C.text1, display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 6 }}>
                    <input type="checkbox" checked={s.fitScale} onChange={e => set({ fitScale: e.target.checked })} />
                    Stretch to fit both points
                  </label>
                </div>
              </>
            )}

            {s.mode === 'origin_azimuth' && (
              <>
                <div>
                  <label style={lbl} htmlFor="anchor-electrode">Anchor electrode (the one whose position you know)</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select id="anchor-electrode" value={s.originElectrode} onChange={e => set({ originElectrode: e.target.value })} style={inp}>{electrodeOptions}</select>
                    {anchorQuick.map(([k, text, el]) => {
                      const on = !!el && String(s.originElectrode) === String(el.id);
                      return (
                        <button key={k} type="button" data-testid={`anchor-${k}`} aria-pressed={on} disabled={!el} onClick={() => set({ originElectrode: el.id })}
                          style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: 'pointer', background: on ? C.accentBg : C.bg4, color: on ? C.accent : C.text1,
                            border: `1px solid ${on ? C.accentBorder : C.border}` }}>{text}</button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: C.text2 }}>Anchor position</div>
                {pair(s.origin, v => set({ origin: v }), 'origin')}
                <div>
                  <label style={lbl}>Electrode spacing (m)</label>
                  <input type="number" aria-label="Electrode spacing" value={s.anchorSpacing} placeholder={fileSpacing ? String(Math.round(fileSpacing * 1e3) / 1e3) : ''} onChange={e => set({ anchorSpacing: e.target.value })} style={inp} />
                  {electrodes.length > 0 && (
                    <div data-testid="anchor-info" style={{ fontSize: 10, color: C.text2, marginTop: 2 }}>
                      {electrodes.length} electrodes from the file · line length {fmt((electrodes.length - 1) * effSpacing, 2)} m
                    </div>
                  )}
                </div>
                <div>
                  <label style={lbl}>Azimuth (degrees clockwise from north)</label>
                  <input type="number" aria-label="Azimuth" value={s.azimuth} onChange={e => set({ azimuth: e.target.value })} style={inp} />
                  <button type="button" data-testid="pick-azimuth" disabled={!!pickOff} title={pickOff || 'Click the map where the line should point to'}
                    aria-pressed={picking === 'azimuth'} onClick={() => { setPickError(null); setPicking(picking === 'azimuth' ? null : 'azimuth'); }}
                    style={{ marginTop: 4, fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: pickOff ? 'not-allowed' : 'pointer', opacity: pickOff ? 0.5 : 1,
                      background: picking === 'azimuth' ? C.accentBg : C.bg4, color: picking === 'azimuth' ? C.accent : C.text1,
                      border: `1px solid ${picking === 'azimuth' ? C.accentBorder : C.border}` }}>
                    {picking === 'azimuth' ? 'Click the map… (cancel)' : 'Pick azimuth on map'}
                  </button>
                </div>
                <label style={{ fontSize: 11, color: C.text1, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={s.anchorFitSpacing} onChange={e => set({ anchorFitSpacing: e.target.checked })} />
                  Dragging the end also sets the spacing
                </label>
              </>
            )}

            {s.mode === 'gcps' && (
              <div>
                <table style={{ width: '100%', fontSize: 11, color: C.text1, borderCollapse: 'collapse' }}>
                  <thead><tr><th style={{ textAlign: 'left' }}>Electrode</th><th>E</th><th>N</th><th>Residual (m)</th><th /></tr></thead>
                  <tbody>
                    {s.gcps.map((g, i) => (
                      <tr key={i}>
                        <td><select value={g.electrode} onChange={e => set({ gcps: s.gcps.map((q, j) => j === i ? { ...q, electrode: e.target.value } : q) })} style={inp}>{electrodeOptions}</select></td>
                        {['E', 'N'].map(k => (
                          <td key={k}><input type="number" value={g[k]} onChange={e => set({ gcps: s.gcps.map((q, j) => j === i ? { ...q, [k]: e.target.value } : q) })} style={inp} /></td>
                        ))}
                        <td style={{ textAlign: 'center', fontFamily: 'monospace' }}>{fmt(d.residuals?.find(r => String(r.id) === String(g.electrode))?.residual, 3)}</td>
                        <td><button onClick={() => set({ gcps: s.gcps.filter((_, j) => j !== i) })} disabled={s.gcps.length <= 3} aria-label="Remove tie point" style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer' }}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={() => set({ gcps: [...s.gcps, { electrode: electrodes[0]?.id ?? '', E: '', N: '' }] })}
                  style={{ marginTop: 6, fontSize: 11, padding: '4px 8px', borderRadius: 6, background: C.bg4, color: C.text1, border: `1px solid ${C.border}`, cursor: 'pointer' }}>
                  + Add tie point
                </button>
              </div>
            )}

            <div>
              <label style={lbl}>Elevation offset (m) — added to every local elevation</label>
              <input type="number" value={s.elevationOffset} onChange={e => set({ elevationOffset: e.target.value })} style={inp} />
            </div>

            {/* Derived info */}
            <div style={{ fontSize: 11, color: C.text2, fontFamily: 'monospace', lineHeight: 1.7, background: C.bg3, borderRadius: 6, padding: '6px 8px' }}>
              {error ? <span style={{ color: C.text2 }}>{error}</span> : georef && (
                <>
                  CRS {georef.crs} · azimuth {fmt(d.azimuth_deg, 2)}° · scale {fmt(d.scale, 4)}<br />
                  {d.line_length !== undefined && <>tie distance {fmt(d.line_length, 2)} m<br /></>}
                  {sc && <>file length {fmt(sc.file_length, 2)} m · difference {fmt(sc.mismatch_pct, 2)} %<br /></>}
                  {sc?.end_offset !== undefined && <>last electrode lands {fmt(sc.end_offset, 2)} m from the end point<br /></>}
                  {d.rms_residual !== undefined && <>RMS residual {fmt(d.rms_residual, 3)} m</>}
                </>
              )}
            </div>
            {sc?.warning && <div role="alert" style={{ fontSize: 11, color: C.warnText, background: 'rgba(251,191,36,0.1)', borderRadius: 6, padding: '6px 8px' }}>⚠ {sc.warning}</div>}
            {saveError && <div role="alert" style={{ fontSize: 11, color: C.red }}>✗ {saveError}</div>}

            {place && (
              <div data-testid="new-project" style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, fontSize: 11, color: C.text2 }}>
                {createdProject ? (
                  <div>
                    Created project <b style={{ color: C.text0 }}>{createdProject.name}</b>.
                    {moved ? ' The dataset now belongs to it; saving the georeference opens the map on it.' : ''}
                    {moveError && <div role="alert" style={{ color: C.red }}>✗ The dataset was not moved: {moveError}</div>}
                  </div>
                ) : !np ? (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {canMoveLine && (
                      <button type="button" onClick={moveLineHere} disabled={!!pickOff}
                        title={pickOff || (s.mode === 'origin_azimuth' ? 'Set the anchor position to the found place' : 'Move the line so its centre is on the found place')}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: pickOff ? 'not-allowed' : 'pointer', opacity: pickOff ? 0.5 : 1, background: C.bg4, color: C.accent, border: `1px solid ${C.accentBorder}` }}>
                        {s.mode === 'origin_azimuth' ? 'Move anchor here' : 'Move line here'}
                      </button>
                    )}
                    <button type="button" onClick={openNewProject}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: C.bg4, color: C.text1, border: `1px solid ${C.border}` }}>
                      New project here…
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div>
                      <label style={lbl}>Project name</label>
                      <input type="text" aria-label="Project name" value={np.name} onChange={e => setNp({ ...np, name: e.target.value })} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Description (optional)</label>
                      <input type="text" aria-label="Project description" value={np.description} onChange={e => setNp({ ...np, description: e.target.value })} style={inp} />
                    </div>
                    <div style={{ color: C.text2 }}>Location: {place.label}</div>
                    {np.error && <div role="alert" style={{ color: C.red }}>✗ {np.error}</div>}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" onClick={createProject} disabled={np.busy}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: C.bg4, color: C.accent, border: `1px solid ${C.accentBorder}` }}>
                        {np.busy ? 'Creating…' : 'Create project'}
                      </button>
                      <button type="button" onClick={() => setNp(null)} disabled={np.busy}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: C.bg4, color: C.text1, border: `1px solid ${C.border}` }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Live preview */}
          <div style={{ flex: '1 1 380px', minHeight: 320, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, position: 'relative' }}>
            <Map ref={mapRef} onLoad={() => setMapReady(true)} initialViewState={{ longitude: -97.74, latitude: 30.27, zoom: 3 }}
              mapStyle={getViewModeStyle(basemap, theme)} style={{ width: '100%', height: '100%', minHeight: 320 }}
              onClick={handleMapClick} cursor={picking ? 'crosshair' : undefined}
              attributionControl={{ compact: true }}>
              <NavigationControl position="top-right" showCompass={false} />
              {preview && (
                <Source id="georef-preview" type="geojson" data={preview}>
                  <Layer id="georef-preview-fill" type="fill" filter={['==', ['geometry-type'], 'Polygon']}
                    paint={{ 'fill-color': '#00d4ff', 'fill-opacity': 0.12 }} />
                  <Layer id="georef-preview-line" type="line" filter={['!=', ['geometry-type'], 'Point']}
                    paint={{ 'line-color': '#00d4ff', 'line-width': 2 }} />
                  <Layer id="georef-preview-pts" type="circle" filter={['==', ['geometry-type'], 'Point']}
                    paint={{ 'circle-radius': 4, 'circle-color': '#fbbf24', 'circle-stroke-color': '#0d1117', 'circle-stroke-width': 1 }} />
                </Source>
              )}
              {pin && (
                <Marker longitude={pin.lon} latitude={pin.lat} anchor="bottom">
                  <div data-testid="place-pin" title={pin.label}><MapPin size={26} color="#f87171" fill="#f87171" fillOpacity={0.35} /></div>
                </Marker>
              )}
              {markers.map(m => (
                <Marker key={m.key} longitude={m.lon} latitude={m.lat} draggable onDragEnd={(e) => lngLatToField(m.key, e.lngLat)}>
                  <div data-testid={`electrode-marker-${m.key}`} title={`${m.key === 'end' ? 'Last electrode' : m.key === 'start' ? 'First electrode' : 'Anchor'} (drag to move)`}
                    style={{ width: 16, height: 16, borderRadius: '50%', background: m.key === 'end' ? '#34d399' : '#fbbf24', border: '2px solid #0d1117', boxShadow: '0 0 0 1px #fff', cursor: 'grab' }} />
                </Marker>
              ))}
              {farMarker && (
                <Marker longitude={farMarker.lon} latitude={farMarker.lat} draggable onDragEnd={(e) => dragAzimuth(e.lngLat)}>
                  <div data-testid="electrode-marker-azimuth" title="Line direction (drag to rotate about the anchor)"
                    style={{ width: 16, height: 16, borderRadius: '50%', background: '#34d399', border: '2px solid #0d1117', boxShadow: '0 0 0 1px #fff', cursor: 'grab' }} />
                </Marker>
              )}
              {midMarker && (
                <Marker longitude={midMarker.lon} latitude={midMarker.lat} draggable onDragEnd={(e) => dragMid(e.lngLat)}>
                  <div data-testid="electrode-marker-mid" title="Whole line (drag to move)"
                    style={{ width: 14, height: 14, transform: 'rotate(45deg)', background: '#00d4ff', border: '2px solid #0d1117', boxShadow: '0 0 0 1px #fff', cursor: 'grab' }} />
                </Marker>
              )}
            </Map>
            <div role="group" aria-label="Basemap" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${C.border}`, background: 'rgba(13,17,23,0.82)' }}>
              {[['map', 'Street', 'street'], ['satellite', 'Satellite', 'satellite'], ['topo', 'Topo', 'topo']].map(([id, text, tid]) => (
                <button key={id} type="button" data-testid={`basemap-${tid}`} aria-pressed={basemap === id} onClick={() => chooseBasemap(id)}
                  style={{ fontSize: 11, padding: '3px 9px', border: 'none', cursor: 'pointer', background: basemap === id ? 'rgba(0,212,255,0.25)' : 'transparent', color: basemap === id ? '#00d4ff' : '#e6edf3' }}>{text}</button>
              ))}
            </div>
            {picking && (
              <div role="status" style={{ position: 'absolute', bottom: 24, left: 8, fontSize: 12, padding: '4px 10px', borderRadius: 999, background: 'rgba(13,17,23,0.82)', color: '#e6edf3', pointerEvents: 'none' }}>
                {PICK_HINTS[picking]}
              </div>
            )}
            {pickError && !picking && (
              <div role="alert" style={{ position: 'absolute', bottom: 24, left: 8, fontSize: 12, padding: '4px 10px', borderRadius: 999, background: 'rgba(13,17,23,0.82)', color: '#f87171' }}>{pickError}</div>
            )}
            {!preview && !markers.length && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', padding: 20 }}>
                <span style={{ fontSize: 12, textAlign: 'center', padding: '6px 14px', borderRadius: 999, background: 'rgba(13,17,23,0.78)', color: '#e6edf3', backdropFilter: 'blur(2px)' }}>
                  Enter coordinates to preview the electrodes on the map
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, background: C.bg4, color: C.text1, border: `1px solid ${C.border}`, cursor: 'pointer' }}>
            Keep local
          </button>
          <button onClick={handleSave} disabled={!georef || saving}
            style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, background: georef ? 'linear-gradient(135deg,#0e7490,#1d4ed8)' : C.bg4, color: georef ? '#fff' : C.text3, border: 'none', fontWeight: 600, cursor: georef ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Saving…' : 'Save georeference'}
          </button>
        </div>
      </div>
    </div>
  );
}
